import {
  getEventTypeDef,
  getEventTypeLabel,
  getPersonRoleLabel,
  getVehicleRoleLabel,
} from "./calendarRoles.js";

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addMinutes(date, mins) {
  return new Date(date.getTime() + mins * 60000);
}

export function getEffectiveRange(event) {
  const start = toDate(event?.start || event?.zacatek);
  const end = toDate(event?.end || event?.konec);

  if (!start || !end) {
    return { effectiveStart: null, effectiveEnd: null };
  }

  const typeDef = getEventTypeDef(event?.typUdalosti);
  const before = Number.isFinite(event?.bufferBeforeMin)
    ? event.bufferBeforeMin
    : typeDef.bufferBeforeMin || 0;

  const after = Number.isFinite(event?.bufferAfterMin)
    ? event.bufferAfterMin
    : typeDef.bufferAfterMin || 0;

  return {
    effectiveStart: addMinutes(start, -before),
    effectiveEnd: addMinutes(end, after),
  };
}

export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

export function normalizeRoleAssignments(event) {
  if (Array.isArray(event?.roleAssignments)) {
    return event.roleAssignments
      .filter(Boolean)
      .map((x) => ({
        role: x.role || "",
        personId: x.personId || "",
        personLabel: x.personLabel || x.personName || x.label || x.personId || "",
      }))
      .filter((x) => x.role || x.personId);
  }
  return [];
}

export function normalizeVehicleAssignments(event) {
  if (Array.isArray(event?.vehicleAssignments)) {
    return event.vehicleAssignments
      .filter(Boolean)
      .map((x) => ({
        role: x.role || "",
        vehicleId: x.vehicleId || "",
        vehicleLabel: x.vehicleLabel || x.label || x.vehicleId || "",
      }))
      .filter((x) => x.role || x.vehicleId);
  }
  return [];
}

export function getSharedPersonAssignments(eventA, eventB) {
  const a = normalizeRoleAssignments(eventA);
  const b = normalizeRoleAssignments(eventB);
  const out = [];

  for (const ra of a) {
    for (const rb of b) {
      if (ra.personId && rb.personId && ra.personId === rb.personId) {
        out.push({
          personId: ra.personId,
          personLabel: ra.personLabel || rb.personLabel || ra.personId,
          roleA: ra.role,
          roleB: rb.role,
        });
      }
    }
  }

  return out;
}

export function getSharedVehicleAssignments(eventA, eventB) {
  const a = normalizeVehicleAssignments(eventA);
  const b = normalizeVehicleAssignments(eventB);
  const out = [];

  for (const va of a) {
    for (const vb of b) {
      if (va.vehicleId && vb.vehicleId && va.vehicleId === vb.vehicleId) {
        out.push({
          vehicleId: va.vehicleId,
          vehicleLabel: va.vehicleLabel || vb.vehicleLabel || va.vehicleId,
          roleA: va.role,
          roleB: vb.role,
        });
      }
    }
  }

  return out;
}

export function detectPersonConflict(eventA, eventB) {
  const shared = getSharedPersonAssignments(eventA, eventB);
  if (!shared.length) return null;

  const a = getEffectiveRange(eventA);
  const b = getEffectiveRange(eventB);

  if (!rangesOverlap(a.effectiveStart, a.effectiveEnd, b.effectiveStart, b.effectiveEnd)) {
    return null;
  }

  return {
    type: "person",
    severity: "hard",
    eventAId: eventA.id,
    eventBId: eventB.id,
    eventAType: eventA.typUdalosti,
    eventBType: eventB.typUdalosti,
    eventALabel: getEventTypeLabel(eventA.typUdalosti),
    eventBLabel: getEventTypeLabel(eventB.typUdalosti),
    sharedAssignments: shared,
    reason: "Stejný člověk je obsazen ve dvou událostech ve stejný čas.",
  };
}

export function detectVehicleConflict(eventA, eventB) {
  const shared = getSharedVehicleAssignments(eventA, eventB);
  if (!shared.length) return null;

  const a = getEffectiveRange(eventA);
  const b = getEffectiveRange(eventB);

  if (!rangesOverlap(a.effectiveStart, a.effectiveEnd, b.effectiveStart, b.effectiveEnd)) {
    return null;
  }

  return {
    type: "vehicle",
    severity: "hard",
    eventAId: eventA.id,
    eventBId: eventB.id,
    eventAType: eventA.typUdalosti,
    eventBType: eventB.typUdalosti,
    eventALabel: getEventTypeLabel(eventA.typUdalosti),
    eventBLabel: getEventTypeLabel(eventB.typUdalosti),
    sharedAssignments: shared,
    reason: "Stejné auto je přiřazené do dvou událostí ve stejný čas.",
  };
}

export function detectPlaceConflict(eventA, eventB) {
  const placeA = (eventA?.mistoNazev || "").trim().toLowerCase();
  const placeB = (eventB?.mistoNazev || "").trim().toLowerCase();

  if (!placeA || !placeB || placeA !== placeB) return null;

  const a = getEffectiveRange(eventA);
  const b = getEffectiveRange(eventB);

  if (!rangesOverlap(a.effectiveStart, a.effectiveEnd, b.effectiveStart, b.effectiveEnd)) {
    return null;
  }

  return {
    type: "place",
    severity: "soft",
    eventAId: eventA.id,
    eventBId: eventB.id,
    eventALabel: getEventTypeLabel(eventA.typUdalosti),
    eventBLabel: getEventTypeLabel(eventB.typUdalosti),
    placeLabel: eventA?.mistoNazev || eventB?.mistoNazev || "-",
    reason: "Dvě události jsou ve stejném místě ve stejném čase.",
  };
}

export function detectMissingAssignments(event) {
  const typeDef = getEventTypeDef(event?.typUdalosti);
  const people = normalizeRoleAssignments(event);
  const vehicles = normalizeVehicleAssignments(event);

  const missingPeople = [];
  const missingVehicles = [];

  for (const roleKey of typeDef.requiredPersonRoles || []) {
    const exists = people.some((x) => x.role === roleKey && x.personId);
    if (!exists) {
      missingPeople.push({
        role: roleKey,
        label: getPersonRoleLabel(roleKey),
      });
    }
  }

  for (const roleKey of typeDef.requiredVehicleRoles || []) {
    const exists = vehicles.some((x) => x.role === roleKey && x.vehicleId);
    if (!exists) {
      missingVehicles.push({
        role: roleKey,
        label: getVehicleRoleLabel(roleKey),
      });
    }
  }

  if (!missingPeople.length && !missingVehicles.length) return null;

  return {
    type: "missing_assignments",
    severity: event?.isTentative ? "soft" : "hard",
    eventId: event.id,
    eventLabel: getEventTypeLabel(event?.typUdalosti),
    missingPeople,
    missingVehicles,
    reason: "Událost nemá obsazené všechny povinné role.",
  };
}

export function detectCalendarConflicts(events = []) {
  const personConflicts = [];
  const vehicleConflicts = [];
  const placeConflicts = [];
  const missingAssignments = [];

  const safeEvents = Array.isArray(events) ? events.filter(Boolean) : [];

  for (const event of safeEvents) {
    const miss = detectMissingAssignments(event);
    if (miss) missingAssignments.push(miss);
  }

  for (let i = 0; i < safeEvents.length; i += 1) {
    for (let j = i + 1; j < safeEvents.length; j += 1) {
      const a = safeEvents[i];
      const b = safeEvents[j];

      const person = detectPersonConflict(a, b);
      if (person) personConflicts.push(person);

      const vehicle = detectVehicleConflict(a, b);
      if (vehicle) vehicleConflicts.push(vehicle);

      const place = detectPlaceConflict(a, b);
      if (place) placeConflicts.push(place);
    }
  }

  return {
    personConflicts,
    vehicleConflicts,
    placeConflicts,
    missingAssignments,
    all: [
      ...personConflicts,
      ...vehicleConflicts,
      ...placeConflicts,
      ...missingAssignments,
    ],
  };
}
